import {
  LiveKitRoom,
  RoomAudioRenderer,
  useVoiceAssistant,
  useLocalParticipant,
  useRoomContext,
  useTranscriptions,
  useConnectionState,
  useMediaDeviceSelect,
  useTrackVolume,
  useMultibandTrackVolume,
} from "@livekit/components-react";
import { useState, useEffect, useRef, useCallback, useId } from "react";
import { Track, ConnectionState, type LocalAudioTrack } from "livekit-client";
import { uploadAudio, type IntegrityEvent } from "@/api/client";
import { useRoomRecorder } from "@/hooks/use-recorder";
import { useInterviewGuard } from "@/hooks/use-interview-guard";
import { useProctoring } from "@/hooks/use-proctoring";
import { useToast } from "@/hooks/use-toast";
import { ThinkingShimmer } from "@/components/agents/loading-states/thinking-shimmer";
import { VoiceOrb } from "@/components/agents/voice-orb";
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageHeader,
} from "@/components/agents/message";
import { MessageScroller } from "@/components/agents/message-scroller";
import "@/styles/aura-arena.css";

interface InterviewAgentProps {
  token: string;
  sessionId: string;
  candidateName?: string;
  /** Record mic + agent audio in the browser and upload on end (recruiter invites). */
  recordAudio?: boolean;
  /** Collect integrity signals (tab switches, focus loss, paste) for a proctored invite. */
  proctoring?: boolean;
  /** Called once the interview is over — confirmed end or room disconnect.
   *  The report is NOT ready yet: it generates in the background and appears
   *  on the session's report page, which the call site should navigate to. */
  onInterviewEnd: () => void;
}

const METER_BARS = 28;

/** Compact mic selector that fits in the topbar. */
function MicSelector() {
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind: "audioinput", requestPermissions: true });
  const [open, setOpen] = useState(false);
  const filtered = devices.filter((d) => d.deviceId !== "");
  const active = filtered.find((d) => d.deviceId === activeDeviceId);
  const label = active?.label || "Mic";

  if (filtered.length === 0) return null;

  return (
    <div className="mic-select" onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        className="mic-select-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title={label}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0" />
          <path d="M12 18v3" />
        </svg>
        <span className="mic-name">{label.length > 18 ? label.slice(0, 16) + "…" : label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <ul role="listbox" className="mic-menu">
          {filtered.map((d) => (
            <li
              key={d.deviceId}
              role="option"
              aria-selected={d.deviceId === activeDeviceId}
              className={d.deviceId === activeDeviceId ? "selected" : ""}
              onClick={() => {
                setActiveMediaDevice(d.deviceId);
                setOpen(false);
              }}
            >
              {d.label || `Mic (${d.deviceId.slice(0, 8)})`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface ArenaConfirmModalProps {
  open: boolean;
  title: string;
  body: string;
  cancelLabel: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** The arena's confirmation dialog, shared by the leave guard and the
 *  end-interview button. Escape and the cancel button are always the safe
 *  action; focus starts there and Tab cycles within the dialog so focus can't
 *  reach background controls while aria-modal claims them. */
function ArenaConfirmModal({
  open,
  title,
  body,
  cancelLabel,
  confirmLabel,
  destructive = false,
  onConfirm,
  onCancel,
}: ArenaConfirmModalProps) {
  const titleId = useId();
  const bodyId = useId();
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // The interview re-renders its parent many times a second (mic bands, agent
  // volume), so onCancel is a new identity each render. Reading it through a
  // ref and keying this effect on [open] alone keeps the re-render from
  // tearing the focus/keyboard setup down and re-stealing focus to the cancel
  // button — which used to make the confirm button unreachable by keyboard.
  const onCancelRef = useRef(onCancel);
  useEffect(() => {
    onCancelRef.current = onCancel;
  });

  useEffect(() => {
    if (!open) return;
    cancelButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancelRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const modal = modalRef.current;
      if (!modal) return;
      const focusables = Array.from(modal.querySelectorAll<HTMLElement>("button"));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && modal.contains(active);
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="guard-overlay" data-open={open ? "true" : "false"} role="presentation" onClick={onCancel}>
      <div
        className="guard-modal"
        ref={modalRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="guard-icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <h2 id={titleId}>{title}</h2>
        <p id={bodyId}>{body}</p>
        <div className="guard-actions">
          <button ref={cancelButtonRef} className="btn btn-ghost" type="button" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            className={destructive ? "btn btn-danger" : "btn btn-primary"}
            type="button"
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function InterviewInner({ sessionId, candidateName = "Candidate", recordAudio = false, proctoring = false, onInterviewEnd }: { sessionId: string; candidateName?: string; recordAudio?: boolean; proctoring?: boolean; onInterviewEnd: () => void }) {
  const { toast } = useToast();
  const [hasConnected, setHasConnected] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const TOTAL_SECONDS = 10 * 60;
  const WARNING_SECONDS = 2 * 60;
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const [transcriptWidth, setTranscriptWidth] = useState(420);

  // onInterviewEnd is an inline arrow at both call sites, so its identity
  // changes on every parent render. Holding it in a ref keeps the end-flow
  // callbacks from being torn down by parent re-renders — and lets
  // endInterview (memoized on room state only) invoke the latest one.
  const onInterviewEndRef = useRef(onInterviewEnd);
  useEffect(() => {
    onInterviewEndRef.current = onInterviewEnd;
  });

  const roomState = useConnectionState();
  const room = useRoomContext();
  const { state: agentState, audioTrack: agentAudioTrack } = useVoiceAssistant();
  const { localParticipant } = useLocalParticipant();
  const transcriptions = useTranscriptions();

  // Local mic track reference (for volume hooks)
  const micPub = localParticipant?.getTrackPublication(Track.Source.Microphone);
  const micTrack = (micPub?.track as LocalAudioTrack | undefined) ?? undefined;

  // Audio level hooks - real-time from LiveKit
  const userBands = useMultibandTrackVolume(micTrack, { bands: METER_BARS, updateInterval: 50 });
  const agentVolume = useTrackVolume(agentAudioTrack?.publication?.track as any);
  // Aura pigments for the beUI voice orb — the accent family straight from
  // the design tokens (base / highlight / shadow).
  const orbColors = ["#4361eb", "#60a5fa", "#2f47c5"] as const;

  // Browser-side recording (recruiter invite sessions only). The upload runs
  // from onstop rather than a state-watching effect: MediaRecorder fires onstop
  // asynchronously, so reacting to a render would read the blob before it exists.
  const audioUploadedRef = useRef(false);
  const handleRecordingComplete = (recorded: Blob) => {
    if (audioUploadedRef.current || recorded.size === 0) return;
    audioUploadedRef.current = true;
    const ext = recorded.type.includes("mp4") ? "mp4" : "webm";
    uploadAudio(sessionId, recorded, ext).catch((err) => {
      // Reset the guard so the flag never claims a save that didn't happen.
      // (The recorder stops once per session, so this is honest state rather
      // than a live retry path.)
      audioUploadedRef.current = false;
      console.error("Audio upload failed:", err);
      // The recruiter sees only "no recording available" otherwise — make the
      // failure visible to the candidate so it can be reported. Auto-dismiss
      // is off: this toast is the only recording-loss signal on the hand-off
      // surface, so it stays until the candidate dismisses it.
      toast({
        title: "Recording not saved",
        description: "We couldn't upload the interview recording. The report is unaffected — please mention it to your recruiter.",
        variant: "destructive",
        duration: Infinity,
      });
    });
  };

  const { recording, stop: stopRecorder } = useRoomRecorder({
    enabled: recordAudio,
    connected: roomState === ConnectionState.Connected,
    micTrack: micTrack as unknown as Parameters<typeof useRoomRecorder>[0]["micTrack"],
    agentTrack: agentAudioTrack as unknown as Parameters<typeof useRoomRecorder>[0]["agentTrack"],
    onComplete: handleRecordingComplete,
  });

  // Refs
  const orbWrapRef = useRef<HTMLDivElement>(null);
  const meterRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLElement>(null);
  const barsRef = useRef<HTMLSpanElement[]>([]);

  // Map agent state -> design data-state
  const orbState: "idle" | "listening" | "thinking" | "speaking" =
    agentState === "speaking" ? "speaking"
    : agentState === "thinking" ? "thinking"
    : agentState === "listening" ? "listening"
    : "idle";

  // Build meter bars once
  useEffect(() => {
    const meter = meterRef.current;
    if (!meter) return;
    const created: HTMLSpanElement[] = [];
    for (let i = 0; i < METER_BARS; i++) {
      const b = document.createElement("span");
      b.className = "bar";
      meter.appendChild(b);
      created.push(b);
    }
    barsRef.current = created;
    return () => { created.forEach((e) => e.remove()); barsRef.current = []; };
  }, []);

  // Drive meter bars from real user mic frequency bands
  useEffect(() => {
    const bars = barsRef.current;
    if (!bars.length) return;
    for (let i = 0; i < bars.length; i++) {
      const v = muted ? 0 : (userBands[i] ?? 0);
      const h = Math.max(4, Math.min(28, 4 + v * 60));
      bars[i].style.height = `${h.toFixed(1)}px`;
      bars[i].style.opacity = muted ? "0.25" : `${(0.4 + v * 0.7).toFixed(2)}`;
    }
  }, [userBands, muted]);

  // Build transcript messages from real LiveKit data
  const localMicTrackSid = micPub?.trackSid;
  const rawMessages = transcriptions.map((t: any, idx: number) => {
    const tid = t.streamInfo?.attributes?.["lk.transcribed_track_id"];
    return { id: t.streamInfo?.id || `msg-${idx}`, message: t.text || "", isLocal: tid === localMicTrackSid };
  }).filter((m) => m.message);
  const messages = rawMessages.reduce<typeof rawMessages>((acc, msg) => {
    const last = acc[acc.length - 1];
    if (last && last.isLocal === msg.isLocal) last.message += " " + msg.message;
    else acc.push({ ...msg });
    return acc;
  }, []);

  // Track connection
  useEffect(() => {
    if (roomState === ConnectionState.Connected) setHasConnected(true);
  }, [roomState]);

  // Stop the recorder when the room disconnects; the finished blob is uploaded
  // from the recorder's onstop callback (see the hook call above), which fires
  // asynchronously — so nothing here waits on a render to notice it.
  useEffect(() => {
    if (recordAudio && roomState === ConnectionState.Disconnected) {
      stopRecorder();
    }
  }, [roomState, recordAudio, stopRecorder]);

  // Timer — stops as soon as the interview ends. The interval previously keyed
  // only on hasConnected, so the countdown kept ticking over the "interview
  // complete" overlay until the component unmounted.
  useEffect(() => {
    if (!hasConnected || hasEnded) return;
    const i = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(i);
  }, [hasConnected, hasEnded]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const isWarning = remaining <= WARNING_SECONDS && remaining > 0;
  const isCritical = remaining <= 30 && remaining > 0;

  // Mute toggle
  const toggleMute = async () => {
    if (!micTrack) return;
    if (muted) await micTrack.unmute();
    else await micTrack.mute();
    setMuted(!muted);
  };

  // End interview — disconnect the room so the worker marks the session
  // completed and generates the report, then hand the user off immediately:
  // the report lands on the session's report page, which polls it in. The
  // arena itself no longer waits on generation.
  const endInterview = useCallback(async () => {
    setHasEnded(true);
    try { await localParticipant?.setMicrophoneEnabled(false); } catch {}
    try { await room?.disconnect(); } catch (e) { console.error("Room disconnect failed:", e); }
    onInterviewEndRef.current();
  }, [room, localParticipant]);

  // At 0:00 the candidate's clock is spent. The worker's hard cap fires at the
  // same wall-clock moment, but if it hasn't torn the room down yet, end from
  // the client so the candidate lands on the report page instead of staring
  // at a dead timer. No confirmation: there is nothing left to protect.
  // Deferred to a task — effects must not setState synchronously.
  useEffect(() => {
    if (!hasConnected || hasEnded || remaining > 0) return;
    const t = setTimeout(() => void endInterview(), 0);
    return () => clearTimeout(t);
  }, [hasConnected, hasEnded, remaining, endInterview]);

  // Leave guard — active only while the room is live and the user hasn't
  // already ended the session, so previewing and the end-of-interview teardown
  // stay friction-free. A confirmed leave runs endInterview: the worker
  // finalizes the report and the hand-off below navigates to the report page.
  const { confirmOpen: leaveConfirmOpen, requestLeave, resolveLeave } = useInterviewGuard(
    roomState === ConnectionState.Connected && !hasEnded,
    endInterview,
  );

  // Integrity signals (proctored invites only). Same enable window as the
  // guard; the hook flushes its buffer when this window closes, so no
  // explicit flush is needed on the disconnect paths below.
  const warnedTabHiddenRef = useRef(false);
  useProctoring(
    proctoring && roomState === ConnectionState.Connected && !hasEnded,
    sessionId,
    (event: IntegrityEvent) => {
      if (event.type !== "tab_hidden" || warnedTabHiddenRef.current) return;
      warnedTabHiddenRef.current = true;
      toast({
        title: "Stay in this tab",
        description: "Leaving the interview tab is recorded in the interview's integrity report.",
        duration: 6000,
      });
    },
  );

  // A worker-initiated end (the agent's end_interview tool, the hard cap, or
  // the participant-disconnect path) closes the room from under us. Hand the
  // user off to the report page the moment that happens — the report is being
  // generated in the background and the page polls it in.
  useEffect(() => {
    if (hasConnected && roomState === ConnectionState.Disconnected && !hasEnded) {
      setHasEnded(true);
      onInterviewEndRef.current();
    }
  }, [hasConnected, roomState, hasEnded]);

  // Resizable transcript handle
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: transcriptWidth };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const dx = dragRef.current.startX - e.clientX; // dragging left = wider
    const next = Math.max(280, Math.min(720, dragRef.current.startW + dx));
    setTranscriptWidth(next);
  };
  const onDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
  };

  const stateLabel = orbState === "idle" ? "Idle" : orbState === "listening" ? "Listening" : orbState === "thinking" ? "Processing" : "Speaking";
  const initials = candidateName.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "C";
  const sidShort = sessionId.slice(0, 4) + "-" + sessionId.slice(-4);

  return (
    <div className="aura-arena-page">
      <div className="ambient" aria-hidden="true"></div>
      <div className="grid-mesh" aria-hidden="true"></div>

      {/* Top status bar */}
      <header className="topbar" role="banner">
        <div className="top-left">
          <a
            className="brand"
            href="/"
            aria-label="Aura — back to home"
            onClick={(e) => {
              if (roomState === ConnectionState.Connected && !hasEnded) {
                e.preventDefault();
                requestLeave();
              }
            }}
          >
            <span className="mark" aria-hidden="true"></span>
            <span className="text">Aura</span>
          </a>
          <div className="candidate-chip" role="group" aria-label="Candidate">
            <span className="avatar" aria-hidden="true">{initials}</span>
            <span className="name">{candidateName}</span>
          </div>
        </div>

        <div className="top-center">
          {proctoring && (
            <div
              className="session-status proctor-status"
              role="status"
              title="Integrity signals (tab switches, paste attempts) are recorded and shared with the recruiter."
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>Proctoring on</span>
            </div>
          )}
          <div className="session-status" aria-live="polite" aria-atomic="true" data-recording={recording ? "true" : "false"}>
            <span className={recording ? "rec-dot" : "idle-dot"} aria-hidden="true"></span>
            {/* Keyed label: the pill's colors transition via CSS; the text
                swaps with a small rise instead of flickering. */}
            <span key={recording ? 'rec' : 'idle'} className="aura-arena-swap">{recording ? "Recording" : "Session · Active"}</span>
          </div>
        </div>

        <div className="top-right">
          <MicSelector />
          <div className={`timer ${isWarning ? 'timer-warning' : ''} ${isCritical ? 'timer-critical' : ''}`}
               aria-label={`${mm}:${ss} remaining`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>{mm}:{ss}</span>
          </div>
          <div className="session-id" role="group" aria-label="Session identifier">
            <strong>SID</strong> · {sidShort}
          </div>
        </div>
      </header>

      {/* Stage */}
      <main className="stage" style={{ gridTemplateColumns: `1fr ${transcriptWidth}px` }}>
        <section className="orb-zone" aria-label="Aura agent presence">
          <div
            className="orb-wrap"
            ref={orbWrapRef}
            data-state={orbState}
            data-muted={muted ? "true" : "false"}
          >
            {/* beUI voice orb: the WebGL liquid sphere replaces the CSS
                ring/dust/arc rig. Agent audio volume drives the surface
                directly — no per-frame CSS-var plumbing. jsdom (and any
                WebGL-less browser) gets the component's built-in fallback. */}
            <VoiceOrb
              activity={agentVolume}
              colors={orbColors}
              aria-label="Aura"
              className="orb-visual"
            />
          </div>

          <div className="state-plate">
            <span className="pip" aria-hidden="true"></span>
            {/* While Aura works the static word becomes a shimmering line —
                the one state where "something is happening" needs to be felt. */}
            {orbState === "thinking" ? (
              <ThinkingShimmer>Aura is thinking</ThinkingShimmer>
            ) : (
              <span key={stateLabel} className="state-word">{stateLabel}</span>
            )}
          </div>
        </section>

        {/* Transcript with resize handle */}
        <aside className="transcript" ref={transcriptRef} aria-label="Live transcript" style={{ width: "auto" }}>
          <div
            className="transcript-resize"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize transcript"
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            onPointerCancel={onDragEnd}
          />
          <div className="transcript-header">
            <div className="transcript-title">
              <span className="live-pip" aria-hidden="true"></span>
              <span>Live transcript</span>
            </div>
            <div className="transcript-meta">EN · auto</div>
          </div>
          {/* beUI message primitives + reader-aware scroller: the viewport
              keeps the .transcript-body chrome; follow-at-live-edge replaces
              the manual scrollTop effect and releases when the reader scrolls
              up to re-read. */}
          <MessageScroller
            label="Live transcript"
            viewportClassName="transcript-body"
            contentClassName="transcript-list"
          >
            {messages.length === 0 && (
              <div className="aura-arena-swap" style={{ color: "var(--muted-2)", fontSize: 13, textAlign: "center", marginTop: 24, fontStyle: "italic" }}>
                Waiting for conversation…
              </div>
            )}
            {messages.map((m) => (
              <Message key={m.id} from={m.isLocal ? "user" : "assistant"} animateIn>
                <MessageAvatar>{m.isLocal ? "Y" : "A"}</MessageAvatar>
                <MessageContent>
                  <MessageHeader>{m.isLocal ? "You" : "Aura"}</MessageHeader>
                  <div className="msg-text">{m.message}</div>
                </MessageContent>
              </Message>
            ))}
          </MessageScroller>
        </aside>
      </main>

      {/* Bottom action dock */}
      <div className="dock-wrap">
        {!hasEnded && (
          <p className="dock-note" aria-hidden="true">
            Don't refresh or close this tab — the interview ends and the session's credits are used.
          </p>
        )}
        <div className="dock" role="toolbar" aria-label="Interview controls">
          <button
            className={`dock-btn ${muted ? "muted" : ""}`}
            onClick={toggleMute}
            aria-pressed={muted}
            aria-label={muted ? "Unmute microphone" : "Mute microphone"}
            type="button"
          >
            {/* Both mic glyphs stay mounted in one grid cell (.ico-stack in
                aura-arena.css) so the mute toggle is an opacity crossfade
                instead of a display swap. */}
            <span className="ico-stack" aria-hidden="true">
              <svg className="ico ico-mic-on" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="3" width="6" height="12" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0" />
                <path d="M12 18v3" />
              </svg>
              <svg className="ico ico-mic-off" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 3l18 18" />
                <path d="M9 9v3a3 3 0 0 0 4.83 2.36" />
                <path d="M15 12V6a3 3 0 0 0-5.94-.6" />
                <path d="M5 11a7 7 0 0 0 11.28 5.6" />
                <path d="M19 11a7 7 0 0 1-.4 2.34" />
                <path d="M12 18v3" />
              </svg>
            </span>
            <span className="label">{muted ? "Unmute" : "Mute"}</span>
          </button>

          <div className="meter" ref={meterRef} data-muted={muted ? "true" : "false"} aria-hidden="true"></div>

          <button className="dock-btn danger" onClick={() => setEndConfirmOpen(true)} aria-label="End interview" type="button">
            <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3.5 14.5a3 3 0 0 1 0-5L4 9a17 17 0 0 1 16 0l.5.5a3 3 0 0 1 0 5l-2 .8a2 2 0 0 1-2.4-.7l-1-1.4a2 2 0 0 0-1.6-.9h-2a2 2 0 0 0-1.6.9l-1 1.4a2 2 0 0 1-2.4.7Z" />
            </svg>
            <span className="label">End interview</span>
          </button>
        </div>
      </div>

      {/* Leave-intent modal — refreshing or navigating away mid-interview
          disconnects the room, finalizes the report, and spends the session.
          Unmounted while the end-confirm modal is open so only one dialog is
          ever in the a11y tree / listening for Escape; a pending leave intent
          resurfaces the moment the end modal resolves. */}
      {!endConfirmOpen && (
        <ArenaConfirmModal
          open={leaveConfirmOpen}
          title="Leave the interview?"
          body="Refreshing, closing this tab, or navigating away ends the interview now. The report is generated from what's been said so far, and this session's credits are spent either way."
          cancelLabel="Stay in interview"
          confirmLabel="End & leave"
          destructive
          onConfirm={() => resolveLeave(true)}
          onCancel={() => resolveLeave(false)}
        />
      )}

      {/* End-intent modal — the dock button ends the interview on purpose, so
          it gets the same friction as every other exit. */}
      <ArenaConfirmModal
        open={endConfirmOpen}
        title="End the interview now?"
        body="Aura stops asking questions, and the report is generated from what's been said so far. This session's credits are spent either way."
        cancelLabel="Keep talking"
        confirmLabel="End interview"
        destructive
        onConfirm={() => {
          setEndConfirmOpen(false);
          void endInterview();
        }}
        onCancel={() => setEndConfirmOpen(false)}
      />

      <RoomAudioRenderer />
    </div>
  );
}

export function InterviewAgent({ token, sessionId, candidateName, recordAudio, proctoring, onInterviewEnd }: InterviewAgentProps) {
  const serverUrl = import.meta.env.VITE_LIVEKIT_URL;
  if (!serverUrl) return <div style={{ padding: 32, textAlign: "center", color: "#ef4444" }}>VITE_LIVEKIT_URL is missing in .env</div>;
  return (
    // disconnectOnPageLeave defaults to true, which disconnects the room the
    // moment a reload is *attempted* — a candidate who picks "Stay on page"
    // in the beforeunload dialog came back to a dead interview. The guard's
    // own native dialog is the only exit prompt we need; a real page unload
    // still drops the socket and the worker finalizes as before.
    <LiveKitRoom
      serverUrl={serverUrl}
      token={token}
      connect={true}
      audio={true}
      video={false}
      options={{ disconnectOnPageLeave: false }}
      onError={(err) => console.error("LiveKit Error:", err)}
    >
      <InterviewInner sessionId={sessionId} candidateName={candidateName} recordAudio={recordAudio} proctoring={proctoring} onInterviewEnd={onInterviewEnd} />
    </LiveKitRoom>
  );
}
